import { TypedDocumentNode, gql } from '@apollo/client';
import { CSSProperties, Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { animated, config, useSpring } from 'react-spring';
import { useMeasure } from 'react-use';
import styled, { css } from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { getHumanReadableQuality } from '@sorare/core/src/lib/cards';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { CraftingButton } from '../CraftingButton';
import { CraftingMachine_cardShardsChest } from './__generated__/index.graphql';
import footballBg from './assets/football-bg.jpg';
import nbaBg from './assets/nba-bg.jpg';

const backgrounds = {
  [Sport.FOOTBALL]: footballBg,
  [Sport.NBA]: nbaBg,
  // TODO  Update BASEBALL with proper assets when available
  [Sport.BASEBALL]: nbaBg,
};

const SHARDS_SCARCITIES = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
] as const;

const isExistingScarcity = (
  type: string
): type is (typeof SHARDS_SCARCITIES)[number] =>
  (Object.values(SHARDS_SCARCITIES) as string[]).includes(type);

const textColorPerRarity = {
  [Rarity.limited]: '255, 199, 0',
  [Rarity.rare]: '255, 46, 0',
  [Rarity.super_rare]: '0, 178, 255',
  [Rarity.unique]: '143, 0, 255',
};

const gradientIdPerRarity = {
  [Rarity.limited]: 'linear_gradient_liquid_limited',
  [Rarity.rare]: 'linear_gradient_liquid_rare',
  [Rarity.super_rare]: 'linear_gradient_liquid_superRare',
  [Rarity.unique]: 'linear_gradient_liquid_unique',
};

const glowIdPerRarity = {
  [Rarity.limited]: 'filter_glow_limited',
  [Rarity.rare]: 'filter_glow_rare',
  [Rarity.super_rare]: 'filter_glow_superRare',
  [Rarity.unique]: 'filter_glow_unique',
};

const StyledSvg = styled.svg`
  position: absolute;
  inset: 0;
  color: rgb(var(--rgb-rarity-color));
`;

const CraftingButtonWrapper = styled.div`
  position: absolute;
  bottom: 10%;
  left: 50%;
  transform: translateX(-50%);
  width: min-content;
`;

const Count = styled(Horizontal).attrs({ center: true })`
  height: 100%;
  align-items: center;
`;
const textGlow = css`
  text-shadow:
    0 0 20px rgb(var(--rgb-rarity-color)),
    0 0 30px rgba(var(--rgb-rarity-color), 70%);
`;
const StyledBodyM = styled(BodyM)`
  font-size: 35pt;
  ${textGlow}
`;
const StyledBodyS = styled(BodyS)`
  font-size: 26pt;
  ${textGlow}
`;
const StyledShards = styled(Shards)`
  filter: drop-shadow(0 0 15px rgb(var(--rgb-rarity-color)));
`;

const SVG_WIDTH = 1922;
const SVG_HEIGHT = 1773;

const SVG_MIN_HEIGHT = 380;

type Props = {
  cardShardsChest: CraftingMachine_cardShardsChest;
  onCraft: () => Promise<null | (() => void)>;
};

export const CraftingMachine = ({ cardShardsChest, onCraft }: Props) => {
  const {
    flags: { useCraftingAvailableOnNba = false },
  } = useFeatureFlags();
  const { sport = Sport.FOOTBALL } = useSportContext();
  const bg = backgrounds[sport];
  const isTabletAndAbove = useIsTabletAndAbove();
  const [forcedShardsCount, setForcedShardsCount] = useState<number>();
  const { rarity, cardShardsCount, thresholds } = cardShardsChest;

  const [svgRef, { height }] = useMeasure<SVGSVGElement>();

  const SVG_OFFSET_Y = 200;

  const [loading, setLoading] = useState(false);

  const sortedThresholds = sortBy(
    threshold => threshold.threshold,
    [...thresholds]
  );
  const maxTreshold = sortedThresholds.at(-1);

  const maxShardsCount = 1.1 * (maxTreshold?.threshold || 0);
  const steps = [...sortedThresholds, { threshold: maxShardsCount }];

  const actualShardsCount = forcedShardsCount || cardShardsCount;

  const stepsCompletion = steps.map((step, i, array) => {
    const startThreshold = array[i - 1]?.threshold ?? 0;

    return Math.max(
      0,
      Math.min(
        1,
        (actualShardsCount - startThreshold) / (step.threshold - startThreshold)
      )
    );
  });

  const { percent } = useSpring({
    from: {
      percent: 0,
    },
    percent: stepsCompletion.reduce(
      (acc, stepCompletion) => acc + stepCompletion * (1 / steps.length),
      0
    ),
    config: config.molasses,
  });

  if (!isExistingScarcity(rarity)) {
    return null;
  }
  const textColor = textColorPerRarity[rarity];
  const gradientId = gradientIdPerRarity[rarity];

  const craftThresholdIndex = [...stepsCompletion]
    .reverse()
    .slice(1, thresholds.length + 1)
    .findIndex(step => step === 1);
  const craftThreshold =
    craftThresholdIndex !== null
      ? sortedThresholds[sortedThresholds.length - 1 - craftThresholdIndex]
      : null;

  const gaugeHeight = 810;
  const gaugeYTop = 347;
  const gaugeYBottom = gaugeHeight + gaugeYTop - 3;

  return (
    <>
      <StyledSvg
        ref={svgRef}
        viewBox={`0 ${SVG_OFFSET_Y} ${SVG_WIDTH} ${
          SVG_HEIGHT - SVG_OFFSET_Y * 2
        }`}
        width="100%"
        height="100%"
        fill="none"
        preserveAspectRatio={`xMidYMid ${
          height - (isTabletAndAbove ? SVG_OFFSET_Y : 0) < SVG_MIN_HEIGHT
            ? 'meet'
            : 'slice'
        }`}
        style={{ '--rgb-rarity-color': textColor } as CSSProperties}
      >
        <rect width="1922" height="1772.34" fill="url(#pattern0_4504_4845)" />
        <rect
          x="867.998"
          y="344.622"
          width="180.07"
          height="809.369"
          rx="3.83859"
          fill="url(#paint3_linear_4504_4845)"
        />

        <g filter={`url(#${glowIdPerRarity[rarity]})`}>
          <animated.rect
            x="867.998"
            y={344}
            width="180.07"
            height={percent.to(x => x * gaugeHeight)}
            rx="4.11016"
            fill={`url(#${gradientId})`}
            transform={percent.to(
              x => `translate(0 ${gaugeHeight - gaugeHeight * x})`
            )}
          />
        </g>
        {sortedThresholds.map((threshold, i) => {
          const gaugeSize = gaugeYBottom - gaugeYTop;
          const thresholdPercent = (i + 1) / (thresholds.length + 1);
          const value = thresholdPercent * gaugeSize;
          const y = gaugeYBottom - value;

          const sortedByQuality = sortBy(
            p => p.quality,
            [...threshold.probabilities]
          ).reverse();

          if (!sortedByQuality[0]) return null;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={i}>
              <line
                x1="869"
                y1={y}
                x2="916"
                y2={y}
                stroke={stepsCompletion[i] === 1 ? '#fff' : '#959595'}
                strokeWidth="1.27953"
              />
              <line
                x1="1000"
                y1={y}
                x2="1047"
                y2={y}
                stroke={stepsCompletion[i] === 1 ? '#fff' : '#959595'}
                strokeWidth="1.27953"
              />
              {craftThreshold?.threshold === threshold.threshold && (
                <rect
                  x="915"
                  y={y - 45}
                  width="90"
                  height="90"
                  rx="100"
                  fill={`url(#${gradientId})`}
                  stroke="white"
                />
              )}
              <text
                transform={`translate(960 ${y - 40})`}
                fill="#FFF"
                xmlSpace="preserve"
                fontFamily="Helvetica"
                fontSize="26"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                <tspan x="0.254488" y="26">
                  {getHumanReadableQuality(sortedByQuality[0].quality)}+
                </tspan>
              </text>
              <text
                transform={`translate(960 ${y - 6})`}
                fill="#FFF"
                xmlSpace="preserve"
                fontFamily="Helvetica"
                fontSize="26"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                <tspan x="0.254488" y="26">
                  {threshold.threshold}
                </tspan>
              </text>
            </Fragment>
          );
        })}
        <foreignObject x="600" y="630" width="220" height="60">
          <Count>
            <StyledBodyS as="p" color="currentColor" bold>
              <FormattedMessage {...scarcityMessages[rarity]} />
            </StyledBodyS>
          </Count>
        </foreignObject>
        <foreignObject x="1118" y="940" width="165" height="80">
          <Count>
            <StyledBodyM as="p" color="currentColor" bold>
              {actualShardsCount}
            </StyledBodyM>
            <StyledShards size={6} />
          </Count>
        </foreignObject>
        <defs>
          <pattern
            id="pattern0_4504_4845"
            patternContentUnits="objectBoundingBox"
            width="1"
            height="1"
          >
            <use
              xlinkHref="#bg"
              transform="matrix(0.000569217 0 0 0.000617284 -0.0464481 0)"
            />
          </pattern>
          {rarity === Rarity.limited && (
            <filter
              id={glowIdPerRarity[Rarity.limited]}
              x="780"
              y="250"
              width="355"
              height="1000"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="5.48021" />
              <feGaussianBlur stdDeviation="43.8417" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1 0 0 0 0 0.831333 0 0 0 0 0.233333 0 0 0 0.5 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_4504_4845"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_4504_4845"
                result="shape"
              />
            </filter>
          )}
          {rarity === Rarity.rare && (
            <filter
              id={glowIdPerRarity[Rarity.rare]}
              x="780"
              y="250"
              width="355"
              height="1000"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="4.31653" />
              <feGaussianBlur stdDeviation="34.5322" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1 0 0 0 0 0.180392 0 0 0 0 0 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_4504_4150"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_4504_4150"
                result="shape"
              />
            </filter>
          )}
          {rarity === Rarity.super_rare && (
            <filter
              id={glowIdPerRarity[Rarity.super_rare]}
              x="780"
              y="250"
              width="355"
              height="1000"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="4.3165" />
              <feGaussianBlur stdDeviation="34.532" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0.4 0 0 0 0 1 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_4504_4890"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_4504_4890"
                result="shape"
              />
            </filter>
          )}
          {rarity === Rarity.unique && (
            <filter
              id={glowIdPerRarity[Rarity.unique]}
              x="780"
              y="250"
              width="355"
              height="1000"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="3.93853" />
              <feGaussianBlur stdDeviation="34.4622" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.502028 0 0 0 0 0.202431 0 0 0 0 0.883333 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_4504_4931"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_4504_4931"
                result="shape"
              />
            </filter>
          )}
          <filter
            id="filter1_d_4504_4845"
            x="610"
            y="640.208"
            width="205"
            height="40"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1.32934" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_4504_4845"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_4504_4845"
              result="shape"
            />
          </filter>
          <linearGradient
            id="paint3_linear_4504_4845"
            x1="923.247"
            y1="308.856"
            x2="1063.07"
            y2="1116.83"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.0301014" stopOpacity="0" />
            <stop offset="1" />
          </linearGradient>
          <linearGradient
            id={gradientIdPerRarity[Rarity.limited]}
            x1="1062.36"
            y1="960.91"
            x2="870.127"
            y2="964.395"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#EC9A39" />
            <stop offset="0.485" stopColor="#FFC700" />
            <stop offset="1" stopColor="#EC9A39" />
          </linearGradient>
          <linearGradient
            id={gradientIdPerRarity[Rarity.rare]}
            x1="1062.36"
            y1="960.91"
            x2="870.127"
            y2="964.395"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#D90D00" />
            <stop offset="0.485" stopColor="#FF2E00" />
            <stop offset="1" stopColor="#D90D00" />
          </linearGradient>
          <linearGradient
            id={gradientIdPerRarity[Rarity.super_rare]}
            x1="1062.36"
            y1="960.91"
            x2="870.127"
            y2="964.395"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0066FF" />
            <stop offset="0.485" stopColor="#00B3FF" />
            <stop offset="1" stopColor="#0066FF" />
          </linearGradient>
          <linearGradient
            id={gradientIdPerRarity[Rarity.unique]}
            x1="1062.36"
            y1="960.91"
            x2="870.127"
            y2="964.395"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#7000FF" />
            <stop offset="0.485" stopColor="#8F00FF" />
            <stop offset="1" stopColor="#7000FF" />
          </linearGradient>
          <image
            id="bg"
            width="1920"
            height="1620"
            href={bg}
            aria-label="Crafting machine"
          />
        </defs>
      </StyledSvg>
      <CraftingButtonWrapper>
        <CraftingButton
          disabled={
            !craftThreshold ||
            !onCraft ||
            loading ||
            (sport === Sport.NBA && !useCraftingAvailableOnNba)
          }
          onCraft={async () => {
            if (craftThreshold) {
              setLoading(true);
              setForcedShardsCount(cardShardsCount - craftThreshold.threshold);
              const play = await onCraft();
              setLoading(false);
              if (play === null) {
                setForcedShardsCount(undefined);
                return null;
              }
              return () => {
                play();
                setForcedShardsCount(undefined);
              };
            }
            return () => {};
          }}
          availableSoon={sport === Sport.NBA && !useCraftingAvailableOnNba}
        />
      </CraftingButtonWrapper>
    </>
  );
};

CraftingMachine.fragments = {
  cardShardsChest: gql`
    fragment CraftingMachine_cardShardsChest on CardShardsChest {
      id
      rarity
      cardShardsCount
      thresholds {
        probabilities {
          basisPoints
          quality
        }
        threshold
      }
    }
  ` as TypedDocumentNode<CraftingMachine_cardShardsChest>,
};
